import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { G } from "@/brand";
import api from "@/lib/api";
import { Nav } from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import { Gift } from "lucide-react";

const TABS = ["users", "purchases", "feedback", "waitlist"];
const STATUSES = ["new", "in_review", "planned", "done"];

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("users");
  const [rows, setRows] = useState([]);
  const [referrals, setReferrals] = useState(null);

  useEffect(() => {
    if (user === false || (user?.email && user.role !== "admin")) { nav("/"); return; }
    if (user?.role === "admin") {
      api.get("/admin/stats").then(({ data }) => setStats(data));
      api.get("/admin/referrals").then(({ data }) => setReferrals(data)).catch(() => {});
    }
  }, [user, nav]);

  useEffect(() => {
    if (user?.role === "admin") api.get(`/admin/${tab}`).then(({ data }) => setRows(data));
  }, [tab, user]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/feedback/${id}`, { status });
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  if (user?.role !== "admin") return <div style={{ background: G.black, minHeight: "100vh" }}><Nav /></div>;

  const statCards = stats ? [
    ["Users", stats.users], ["Paid Users", stats.paid_users], ["Purchases", stats.purchases],
    ["Revenue", `$${stats.revenue}`], ["Feedback", stats.feedback], ["Prompt Opens", stats.prompt_opens], ["AI Edits", stats.edits], ["Waitlist", stats.waitlist],
  ] : [];

  return (
    <div style={{ background: G.black, minHeight: "100vh" }}>
      <Nav />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "130px 5vw 80px" }}>
        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.5em", color: G.orange, marginBottom: 12 }}>ADMIN CONSOLE</div>
        <h1 style={{ fontSize: "clamp(28px,4vw,44px)", fontWeight: 300, fontStyle: "italic", color: "#fff", marginBottom: 30 }}>Launch Control</h1>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 14, marginBottom: 40 }}>
          {statCards.map(([l, v]) => (
            <div key={l} data-testid={`admin-stat-${l}`} style={{ background: G.dark, border: `1px solid ${G.border}`, padding: 18 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: G.gold }}>{v}</div>
              <div className="mono" style={{ fontSize: 8, letterSpacing: ".15em", color: G.goldDim, marginTop: 4 }}>{l.toUpperCase()}</div>
            </div>
          ))}
        </div>

        {referrals && (
          <div data-testid="admin-referrals" style={{ background: "#16130a", border: `1px solid ${G.border}`, padding: 22, marginBottom: 30 }}>
            <div className="mono" style={{ fontSize: 9, letterSpacing: ".3em", color: G.gold, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <Gift size={12} /> REFERRALS · {referrals.total_referred} INVITED SIGNUPS
            </div>
            {referrals.top_referrers.length === 0 ? (
              <div style={{ color: G.muted, fontStyle: "italic", fontSize: 14 }}>No referred signups yet. Share your link from the Dashboard.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {referrals.top_referrers.map((r, i) => (
                  <div key={r.referrer_id} data-testid={`referrer-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderBottom: `1px solid ${G.border}33`, paddingBottom: 8 }}>
                    <span style={{ fontSize: 14, color: G.text }}><span style={{ color: G.gold }}>#{i + 1}</span> {r.email}</span>
                    <span className="mono" style={{ fontSize: 11, color: G.gold, flexShrink: 0 }}>{r.count} invited</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className="mono"
              style={{ padding: "9px 20px", fontSize: 9, letterSpacing: ".15em", cursor: "pointer", textTransform: "uppercase",
                background: tab === t ? G.gold : "transparent", color: tab === t ? G.black : G.muted, border: `1px solid ${tab === t ? G.gold : G.border}` }}>
              {t}
            </button>
          ))}
        </div>

        <div style={{ background: G.dark, border: `1px solid ${G.border}`, overflowX: "auto" }}>
          {tab === "users" && <Table head={["Email", "Tier", "Credits", "Opens", "Edits", "Role"]}
            body={rows.map((r) => [r.email, r.access_tier, r.credit_balance, r.prompts_opened_count, r.edits_used_count, r.role])} />}
          {tab === "purchases" && <Table head={["Email", "Package", "Amount", "Credits", "Tier", "Date"]}
            body={rows.map((r) => [r.email, r.package_id, `$${r.amount}`, r.credits, r.tier, new Date(r.created_at).toLocaleDateString()])} />}
          {tab === "waitlist" && <Table head={["Email", "App", "Joined"]}
            body={rows.map((r) => [r.email, r.app, new Date(r.created_at).toLocaleDateString()])} />}
          {tab === "feedback" && (
            <div style={{ padding: 4 }}>
              {rows.length === 0 && <div style={{ color: G.muted, fontStyle: "italic", padding: 20 }}>No feedback yet.</div>}
              {rows.map((r) => (
                <div key={r.id} data-testid={`feedback-row-${r.id}`} style={{ borderBottom: `1px solid ${G.border}33`, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <span className="mono" style={{ fontSize: 9, color: G.gold, letterSpacing: ".1em" }}>{r.category?.toUpperCase()} · {r.email}</span>
                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="mono"
                      style={{ background: G.panel, color: G.gold, border: `1px solid ${G.border}`, fontSize: 9, padding: "4px 8px" }}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div style={{ color: G.text, fontSize: 14.5, marginTop: 8 }}>{r.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Table({ head, body }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>{head.map((h) => <th key={h} className="mono" style={{ textAlign: "left", padding: "12px 16px", fontSize: 9, letterSpacing: ".1em", color: G.goldDim, borderBottom: `1px solid ${G.border}` }}>{h.toUpperCase()}</th>)}</tr>
      </thead>
      <tbody>
        {body.length === 0 && <tr><td colSpan={head.length} style={{ padding: 20, color: G.muted, fontStyle: "italic" }}>No records.</td></tr>}
        {body.map((row, i) => (
          <tr key={`${row[0]}-${i}`}>{row.map((c, j) => <td key={head[j]} style={{ padding: "11px 16px", color: G.text, borderBottom: `1px solid ${G.border}22` }}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}
